import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, Search, ShoppingBag, User, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../ui/sheet";
import { useStore } from "../../context/StoreContext";

export const NAV = [
  { to: "/shop", label: "Shop" },
  { to: "/new-arrivals", label: "New Arrivals" },
  { to: "/sneakers", label: "Sneakers" },
  { to: "/men", label: "Men" },
  { to: "/women", label: "Women" },
  { to: "/sale", label: "Sale" },
];

function SearchBar({ onDone }) {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    nav(`/shop?q=${encodeURIComponent(q)}`);
    onDone();
  };
  return (
    <form onSubmit={submit} className="container-x py-3 flex gap-2">
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search sneakers, brands, colours..."
        className="field" data-testid="header-search-input" />
      <button className="btn-gold !px-5" data-testid="header-search-submit">Search</button>
    </form>
  );
}

export function Header() {
  const { settings, cartCount, user } = useStore();
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const linkCls = ({ isActive }) =>
    `text-xs font-bold uppercase tracking-[0.18em] ${isActive ? "text-[#d4af37]" : "text-zinc-300 hover:text-white"}`;
  return (
    <header className="sticky top-0 z-50">
      {settings?.announcement && (
        <div data-testid="announcement-bar" className="gold-gradient text-black text-[11px] sm:text-xs font-extrabold uppercase tracking-[0.2em] text-center py-2 px-4">
          {settings.announcement}
        </div>
      )}
      <div className="bg-[#0a0a0c]/90 backdrop-blur-xl border-b border-white/10">
        <div className="container-x h-16 sm:h-20 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden p-2 -ml-2" aria-label="Open menu" data-testid="mobile-menu-button"><Menu /></button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-[#0a0a0c] border-white/10 w-[85%] max-w-sm">
                <SheetTitle className="sr-only">Menu</SheetTitle>
                <img src="/logo-512.webp" alt="Mwezzy Footwear" className="h-20 w-20 -ml-2" />
                <nav className="mt-6 flex flex-col">
                  {[{ to: "/", label: "Home" }, ...NAV, { to: "/track-order", label: "Track Order" }, { to: "/about", label: "About" }, { to: "/contact", label: "Contact" }, { to: "/account", label: user ? "My Account" : "Login / Register" }].map((n) => (
                    <Link key={n.to} to={n.to} onClick={() => setMenuOpen(false)} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="font-display text-3xl py-3 border-b border-white/5 hover:text-[#d4af37]">{n.label}</Link>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
            <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
              <img src="/logo-512.webp" alt="Mwezzy Footwear logo" className="h-12 w-12 sm:h-16 sm:w-16 object-contain" />
              <span className="hidden sm:block font-display text-2xl leading-none">MWEZZY<span className="block text-xs tracking-[0.4em] text-gold font-sans font-bold">FOOTWEAR</span></span>
            </Link>
          </div>
          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => <NavLink key={n.to} to={n.to} className={linkCls} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`}>{n.label}</NavLink>)}
          </nav>
          <div className="flex items-center gap-1 sm:gap-2">
            <button onClick={() => setSearchOpen((s) => !s)} className="p-2.5 rounded-full hover:bg-white/5" aria-label="Search" data-testid="nav-search-button">
              {searchOpen ? <X size={20} /> : <Search size={20} />}
            </button>
            <Link to="/account" className="p-2.5 rounded-full hover:bg-white/5" aria-label="Account" data-testid="nav-account-button"><User size={20} /></Link>
            <Link to="/cart" className="relative p-2.5 rounded-full hover:bg-white/5" aria-label="Cart" data-testid="nav-cart-button">
              <ShoppingBag size={20} />
              {cartCount > 0 && <span data-testid="nav-cart-count" className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 rounded-full bg-[#d4af37] text-black text-[11px] font-extrabold grid place-items-center">{cartCount}</span>}
            </Link>
          </div>
        </div>
        {searchOpen && <SearchBar onDone={() => setSearchOpen(false)} />}
      </div>
    </header>
  );
}
